import './style.css';

// ==========================================================
// TEMPORARY DEBUG LOGGING — remove after verification
// All logs prefixed with [DEBUG] for easy search & cleanup
// ==========================================================
let _debugFrameCount = 0;
let _debugLastLogTime = 0;
const DEBUG_LOG_INTERVAL = 2000; // Log every 2 seconds (not every frame)

function debugLog(...args) {
    console.log('[DEBUG]', ...args);
}

function debugWarn(...args) {
    console.warn('[DEBUG]', ...args);
}


// DOM Elements
const addressInput = document.getElementById('addressInput');
const browserBack = document.getElementById('browserBack');
const browserForward = document.getElementById('browserForward');
const browserReload = document.getElementById('browserReload');

const startPage = document.getElementById('startPage');
const searchResults = document.getElementById('searchResults');
const playerSection = document.getElementById('playerSection');

// External Controls
const connectAudioBtn = document.getElementById('connectAudioBtn');
const audioBtnText = document.getElementById('audioBtnText');
const audioStatusDot = document.getElementById('audioStatusDot');
const audioStatusText = document.getElementById('audioStatusText');
const bpmValue = document.getElementById('bpmValue');
const beatCount = document.getElementById('beatCount');
const beatIndicator = document.getElementById('beatIndicator');
const phraseValue = document.getElementById('phraseValue');
const canvas = document.getElementById('visualizerCanvas');
const canvasCtx = canvas.getContext('2d');
const timelineCanvas = document.getElementById('beatTimeline');
const timelineCtx = timelineCanvas ? timelineCanvas.getContext('2d') : null;
const suggestedDancesList = document.getElementById('suggestedDancesList');
const rhythmDisplay = document.getElementById('rhythmDisplay');
const danceStyleName = document.getElementById('danceStyleName');
const rhythmCard = document.getElementById('rhythmCard');

// Search API — routes through the Cloudflare Worker proxy (functions/api/search.js).
// The YouTube API key is stored as a Cloudflare secret — never exposed in the browser.
const SEARCH_API_URL = '/api/search';

const DANCE_LIBRARY = [
    {
        name: "Salsa",
        minBpm: 150,
        maxBpm: 220,
        rhythm: ["Quick", "Quick", "Slow", "- ", "Quick", "Quick", "Slow", "- "],
        beatsPerMeasure: 8
    },
    {
        name: "Cha Cha",
        minBpm: 110,
        maxBpm: 135,
        rhythm: ["Slow", "Slow", "Quick", "Quick", "Slow"],
        beatsPerMeasure: 4 // Usually 2, 3, 4&1, but mapped to 1-2-3-4-5 display
    },
    {
        name: "Bachata",
        minBpm: 120,
        maxBpm: 155,
        rhythm: ["1", "2", "3", "Tap"],
        beatsPerMeasure: 4
    },
    {
        name: "Merengue",
        minBpm: 130,
        maxBpm: 200,
        rhythm: ["1", "2", "1", "2"],
        beatsPerMeasure: 4
    },
    {
        name: "Waltz",
        minBpm: 80,
        maxBpm: 98,
        rhythm: ["1", "2", "3"],
        beatsPerMeasure: 3
    },
    {
        name: "Tango",
        minBpm: 115,
        maxBpm: 130,
        rhythm: ["Slow", "Slow", "Quick", "Quick", "Slow"],
        beatsPerMeasure: 4
    },
    {
        name: "Swing (East Coast)",
        minBpm: 135,
        maxBpm: 170,
        rhythm: ["Triple", "Triple", "Rock", "Step"],
        beatsPerMeasure: 4
    },
    {
        name: "Country Two-Step",
        minBpm: 140,
        maxBpm: 200,
        rhythm: ["Quick", "Quick", "Slow", "Slow"],
        beatsPerMeasure: 6
    }
];

// State
let videoId = null;
let audioContext = null;
let analyser = null;
let browserHistory = [];
let historyIndex = -1;
let isNavigating = false;

// Beat Detection State
let beatTimes = [];           // Timestamps of detected beats
let lastBeatTime = 0;
let measureCount = 0;
let prevEnergy = 0;           // Previous frame's bass energy
let energyHistory = [];       // Rolling window of energy values
let fluxHistory = [];         // Rolling window of spectral flux values
const ENERGY_HISTORY_SIZE = 43; // ~0.7s at 60fps
const FLUX_HISTORY_SIZE = 60;  // ~1s at 60fps — for standard deviation calc
const SILENCE_THRESHOLD = 5;  // Min avg amplitude to count as "audio playing"
const BEAT_COOLDOWN_MS = 280; // Min ms between beats (~214 BPM max)
let isBeatFrame = false;      // Whether current frame detected a beat
let silenceStart = 0;         // Timestamp when silence began
let isInSilence = false;      // Whether we're currently in a silent period
let beatTimeline = [];         // Array of {time, strength} for timeline visualization
const TIMELINE_DURATION = 8000; // Show last 8 seconds of beats
let isVideoPlaying = false;
let audioConnectPrompted = false; // Only prompt once per session
let activeDance = null;
let currentBpm = 0;

// Initialization
async function init() {
    debugLog('=== init() called ===');
    debugLog('Current Origin:', window.location.origin);

    // Verify all DOM elements exist
    const domChecks = {
        addressInput: !!addressInput,
        connectAudioBtn: !!connectAudioBtn,
        audioBtnText: !!audioBtnText,
        audioStatusDot: !!audioStatusDot,
        audioStatusText: !!audioStatusText,
        bpmValue: !!bpmValue,
        beatCount: !!beatCount,
        beatIndicator: !!beatIndicator,
        phraseValue: !!phraseValue,
        canvas: !!canvas,
        canvasCtx: !!canvasCtx,
    };
    debugLog('DOM elements found:', domChecks);

    const missingElements = Object.entries(domChecks).filter(([k, v]) => !v).map(([k]) => k);
    if (missingElements.length > 0) {
        debugWarn('⚠️ MISSING DOM ELEMENTS:', missingElements);
    } else {
        debugLog('✅ All DOM elements found successfully');
    }

    addressInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') handleAddressBar();
    });

    browserBack.addEventListener('click', goBack);
    browserForward.addEventListener('click', goForward);
    browserReload.addEventListener('click', reloadPage);

    connectAudioBtn.addEventListener('click', () => {
        debugLog('Connect Audio button clicked');
        initAudio();
    });

    // Responsive Canvas
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initial State
    addToHistory({ type: 'start' });
    debugLog('=== init() complete ===');
}

function resizeCanvas() {
    if (canvas && canvas.parentElement) {
        canvas.width = canvas.parentElement.clientWidth;
        canvas.height = canvas.parentElement.clientHeight;
    }

    if (timelineCanvas && timelineCanvas.parentElement) {
        timelineCanvas.width = timelineCanvas.parentElement.clientWidth;
        timelineCanvas.height = timelineCanvas.parentElement.clientHeight;
    }
}

// --- Browser Navigation Logic ---

function addToHistory(state) {
    if (isNavigating) return;

    // Remove forward history if we branch out
    if (historyIndex < browserHistory.length - 1) {
        browserHistory = browserHistory.slice(0, historyIndex + 1);
    }

    browserHistory.push(state);
    historyIndex++;
    updateUI(state);
    updateNavButtons();
}

function goBack() {
    if (historyIndex > 0) {
        isNavigating = true;
        historyIndex--;
        const state = browserHistory[historyIndex];
        updateUI(state);
        updateNavButtons();
        isNavigating = false;
    }
}

function goForward() {
    if (historyIndex < browserHistory.length - 1) {
        isNavigating = true;
        historyIndex++;
        const state = browserHistory[historyIndex];
        updateUI(state);
        updateNavButtons();
        isNavigating = false;
    }
}

function updateNavButtons() {
    browserBack.disabled = historyIndex <= 0;
    browserForward.disabled = historyIndex >= browserHistory.length - 1;
}

function reloadPage() {
    const state = browserHistory[historyIndex];
    updateUI(state);
}

function updateUI(state) {
    // Hide all views
    startPage.classList.add('hidden');
    searchResults.classList.add('hidden');
    playerSection.classList.add('hidden');

    if (state.type === 'start') {
        startPage.classList.remove('hidden');
        addressInput.value = '';
    } else if (state.type === 'search') {
        searchResults.classList.remove('hidden');
        addressInput.value = state.query || '';
        if (state.results) {
            displayResults(state.results); // Redisplay cached results
        } else {
            performSearch(state.query); // Re-fetch
        }
    } else if (state.type === 'video') {
        playerSection.classList.remove('hidden');
        addressInput.value = `https://youtube.com/watch?v=${state.videoId}`;
        loadVideo(state.videoId);
    }
}

async function handleAddressBar() {
    const input = addressInput.value.trim();
    if (!input) return;

    // Check if URL
    const urlMatch = input.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);

    if (urlMatch && urlMatch[1]) {
        // It's a YouTube URL
        addToHistory({ type: 'video', videoId: urlMatch[1] });
    } else {
        // It's a Search Term
        addToHistory({ type: 'search', query: input });
    }
}

// --- YouTube Logic ---

async function performSearch(query) {
    searchResults.innerHTML = '<p style="text-align:center; color:#888;">Searching...</p>';

    try {
        const response = await fetch(`${SEARCH_API_URL}?q=${encodeURIComponent(query)}&maxResults=12`);
        const data = await response.json();

        if (!response.ok) {
            const errMsg = data.error || 'Search failed. Check your API key configuration.';
            console.error('[Search Error]', errMsg);
            searchResults.innerHTML = `<p style="text-align:center; color:#ef4444; padding: 24px;">${errMsg}</p>`;
            return;
        }

        // Update current history state with results cache
        browserHistory[historyIndex].results = data.items;
        displayResults(data.items);

    } catch (error) {
        console.error(error);
        searchResults.innerHTML = '<p style="text-align:center; color:#ef4444; padding: 24px;">Network error. Is the server running?</p>';
    }
}

function displayResults(items) {
    searchResults.innerHTML = '';
    if (!items || items.length === 0) {
        searchResults.innerHTML = '<p style="text-align:center;">No results found.</p>';
        return;
    }

    items.forEach(item => {
        const title = item.snippet.title;
        const thumbnail = item.snippet.thumbnails.medium.url;
        const id = item.id.videoId;
        const channel = item.snippet.channelTitle;

        const card = document.createElement('div');
        card.className = 'video-card animate-in';
        const safeTitle = title.replace(/"/g, '&quot;');
        card.innerHTML =
            '<div class="video-thumbnail-wrapper">' +
            '<img src="' + thumbnail + '" alt="' + safeTitle + '" loading="lazy">' +
            '<div class="video-play-overlay">' +
            '<div class="play-btn-circle">' +
            '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>' +
            '</div>' +
            '</div>' +
            '</div>' +
            '<div class="video-info">' +
            '<h3 title="' + safeTitle + '">' + title + '</h3>' +
            '<p>' + channel + '</p>' +
            '</div>';

        card.addEventListener('click', () => {
            addToHistory({ type: 'video', videoId: id });
        });
        searchResults.appendChild(card);
    });
}

let player;
function loadVideo(id) {
    videoId = id;
    if (player && typeof player.loadVideoById === 'function') {
        player.loadVideoById(videoId);
    } else {
        // Initial Load
        player = new YT.Player('player', {
            height: '100%',
            width: '100%',
            videoId: videoId,
            events: {
                'onReady': (event) => event.target.playVideo(),
                'onStateChange': onPlayerStateChange
            }
        });
    }
}

function onPlayerStateChange(event) {
    // YT.PlayerState: PLAYING=1, PAUSED=2, ENDED=0, BUFFERING=3
    if (event.data === 1) {
        isVideoPlaying = true;
        debugLog('Video state: PLAYING');
        // Auto-prompt for audio connection if not connected
        if (!audioContext && !audioConnectPrompted) {
            audioConnectPrompted = true;
            debugLog('Auto-prompting for audio connection...');
            initAudio();
        }
    } else {
        isVideoPlaying = false;
        debugLog('Video state:', event.data === 2 ? 'PAUSED' : event.data === 0 ? 'ENDED' : 'BUFFERING');
    }
}

// --- Audio Analysis Logic ---

async function initAudio() {
    debugLog('=== initAudio() called ===');

    if (audioContext) {
        debugLog('AudioContext already exists, state:', audioContext.state);
        if (audioContext.state === 'suspended') {
            debugLog('Resuming suspended AudioContext...');
            await audioContext.resume();
            debugLog('AudioContext resumed. State:', audioContext.state);
            updateAudioStatus(true);
        }
        return;
    }

    try {
        connectAudioBtn.disabled = true;
        audioBtnText.textContent = 'Connecting...';

        let stream;
        debugLog('Requesting getDisplayMedia...');
        try {
            stream = await navigator.mediaDevices.getDisplayMedia({
                video: { displaySurface: "browser" },
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                },
                preferCurrentTab: true
            });
        } catch (err) {
            debugWarn('getDisplayMedia cancelled/failed:', err.name, err.message);
            connectAudioBtn.disabled = false;
            audioBtnText.textContent = 'Connect Audio';
            return;
        }

        // Log stream info
        const audioTracks = stream.getAudioTracks();
        const videoTracks = stream.getVideoTracks();
        debugLog('Stream obtained!');
        debugLog('  Audio tracks:', audioTracks.length, audioTracks.map(t => `${t.label} (${t.readyState})`));
        debugLog('  Video tracks:', videoTracks.length);

        if (audioTracks.length === 0) {
            debugWarn('⚠️ NO AUDIO TRACKS in stream! Did you check "Also share tab audio"?');
        }

        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        debugLog('AudioContext created. State:', audioContext.state, 'Sample rate:', audioContext.sampleRate);

        analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.6;
        debugLog('Analyser created. fftSize:', analyser.fftSize, 'frequencyBinCount:', analyser.frequencyBinCount, 'smoothing:', analyser.smoothingTimeConstant);

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        debugLog('MediaStreamSource → Analyser connected');

        // Do NOT connect to destination — YouTube already plays the audio.

        debugLog('Starting drawVisualizer loop...');
        drawVisualizer();
        updateAudioStatus(true);
        connectAudioBtn.classList.add('connected');
        connectAudioBtn.disabled = false;
        audioBtnText.textContent = 'Audio Connected';
        debugLog('=== initAudio() complete ✅ ===');

    } catch (error) {
        debugWarn('❌ Audio Init Error:', error);
        console.error('Audio Init Error:', error);
        alert('Could not access audio source.');
        connectAudioBtn.disabled = false;
        audioBtnText.textContent = 'Connect Audio';
        updateAudioStatus(false);
    }
}

function updateAudioStatus(active) {
    if (active) {
        audioStatusDot.classList.add('active');
        audioStatusText.textContent = 'Audio Active';
        audioStatusText.style.color = '#10B981'; // var(--success)
    } else {
        audioStatusDot.classList.remove('active');
        audioStatusText.textContent = 'Audio Inactive';
        audioStatusText.style.color = '#71717A'; // var(--text-muted)
    }
}

// --- Energy-Based Beat Detection ---

function detectBeat(dataArray, bufferLength) {
    // Compute bass energy (first ~15% of bins = low frequencies / kick drums)
    const bassEnd = Math.floor(bufferLength * 0.15);
    let bassEnergy = 0;
    for (let i = 0; i < bassEnd; i++) {
        bassEnergy += dataArray[i];
    }
    bassEnergy /= bassEnd;

    // Compute overall average to check for silence
    let totalAvg = 0;
    for (let i = 0; i < bufferLength; i++) {
        totalAvg += dataArray[i];
    }
    totalAvg /= bufferLength;

    const now = Date.now();
    const shouldLog = (now - _debugLastLogTime) > DEBUG_LOG_INTERVAL;

    // --- Silence Detection ---
    if (totalAvg <= SILENCE_THRESHOLD) {
        if (!isInSilence) {
            silenceStart = now;
            isInSilence = true;
            debugLog('🔇 Entered SILENCE (totalAvg:', totalAvg.toFixed(2), '≤ threshold:', SILENCE_THRESHOLD, ')');
        }
        // After 600ms of silence, reset displays
        if (now - silenceStart > 600) {
            bpmValue.textContent = '--';
            beatCount.textContent = '--';
            phraseValue.textContent = '--';
            measureCount = 0;
            beatTimes = [];
            energyHistory = [];
            fluxHistory = [];
            prevEnergy = 0;
        }
        isBeatFrame = false;
        return { bassEnergy, totalAvg, threshold: 0, isOnset: false };
    } else {
        if (isInSilence) {
            debugLog('🔊 Exited SILENCE (totalAvg:', totalAvg.toFixed(2), '> threshold:', SILENCE_THRESHOLD, ')');
        }
        isInSilence = false;
    }

    // --- Spectral Flux Beat Detection ---
    // Instead of comparing bass to its own average (which self-inflates),
    // detect the RATE OF CHANGE (flux) in bass energy.
    // A beat = a sudden SPIKE in flux above the standard deviation.
    const flux = Math.max(0, bassEnergy - prevEnergy); // Only positive changes (onsets)

    fluxHistory.push(flux);
    if (fluxHistory.length > FLUX_HISTORY_SIZE) fluxHistory.shift();

    energyHistory.push(bassEnergy);
    if (energyHistory.length > ENERGY_HISTORY_SIZE) energyHistory.shift();

    // Calculate mean and standard deviation of flux
    const fluxMean = fluxHistory.reduce((a, b) => a + b, 0) / fluxHistory.length;
    const fluxVariance = fluxHistory.reduce((sum, val) => sum + (val - fluxMean) ** 2, 0) / fluxHistory.length;
    const fluxStdDev = Math.sqrt(fluxVariance);

    // Beat = flux exceeds mean + 1.2 * stddev (or a minimum floor)
    const fluxThreshold = fluxMean + Math.max(fluxStdDev * 1.2, 5);

    const isOnset = flux > fluxThreshold
        && (now - lastBeatTime) > BEAT_COOLDOWN_MS; // Cooldown

    // Throttled debug log for energy values
    if (shouldLog) {
        _debugLastLogTime = now;
        debugLog(
            `🎵 Energy | bass: ${bassEnergy.toFixed(1)} | flux: ${flux.toFixed(1)} | fluxMean: ${fluxMean.toFixed(1)} | fluxStdDev: ${fluxStdDev.toFixed(1)} | fluxThreshold: ${fluxThreshold.toFixed(1)} | totalAvg: ${totalAvg.toFixed(1)} | onset: ${isOnset}`
        );
    }

    prevEnergy = bassEnergy;

    if (isOnset) {
        const avgEnergy = energyHistory.reduce((a, b) => a + b, 0) / energyHistory.length;
        debugLog(`🥁 BEAT DETECTED! flux=${flux.toFixed(1)} > threshold=${fluxThreshold.toFixed(1)}, bass=${bassEnergy.toFixed(1)}`);

        lastBeatTime = now;
        beatTimes.push(now);

        // Add to beat timeline
        beatTimeline.push({ time: now, strength: flux / (fluxThreshold + 1) });

        // Keep only the last 16 beats for BPM calculation
        if (beatTimes.length > 16) beatTimes.shift();

        // Calculate BPM from beat intervals (median-filtered)
        if (beatTimes.length >= 4) {
            const intervals = [];
            for (let i = 1; i < beatTimes.length; i++) {
                intervals.push(beatTimes[i] - beatTimes[i - 1]);
            }
            const sorted = [...intervals].sort((a, b) => a - b);
            const medianInterval = sorted[Math.floor(sorted.length / 2)];
            const bpm = Math.round(60000 / medianInterval);
            debugLog(`  BPM calc: medianInterval=${medianInterval}ms → ${bpm} BPM (from ${intervals.length} intervals)`);
            if (bpm > 40 && bpm < 220) {
                bpmValue.textContent = bpm;
                if (Math.abs(bpm - currentBpm) > 2) {
                    currentBpm = bpm;
                    updateDanceSuggestions();
                }
            }
        }

        // Update beat counter (1-2-3-4 cycle)
        let count = parseInt(beatCount.textContent);
        if (isNaN(count) || count >= 4) {
            count = 1;
            measureCount++;
            phraseValue.textContent = measureCount;
        } else {
            count++;
        }
        beatCount.textContent = count;

        // Update Rhythm Display if a dance is selected
        if (activeDance) {
            // Calculate absolute beat within the dance phrase size
            const totalBeats = ((measureCount - 1) * 4) + (count - 1);
            updateRhythmDisplay(totalBeats);
        }

        debugLog(`  Count: ${count}/4 | Phrase: ${measureCount}`);

        // Visual flash
        beatIndicator.classList.add('flash');
        setTimeout(() => beatIndicator.classList.remove('flash'), 120);

        // Pulse the BPM card
        const bpmCard = document.getElementById('bpmCard');
        if (bpmCard) {
            bpmCard.classList.add('pulse');
            setTimeout(() => bpmCard.classList.remove('pulse'), 200);
        }

        isBeatFrame = true;
    } else {
        isBeatFrame = false;
    }

    return { bassEnergy, totalAvg, threshold: fluxThreshold, isOnset };
}

// --- Dance & Rhythm Logic ---

function updateDanceSuggestions() {
    // Basic filtering with some buffer
    const suggestions = DANCE_LIBRARY.filter(dance =>
        currentBpm >= (dance.minBpm - 5) && currentBpm <= (dance.maxBpm + 5)
    );

    if (suggestions.length === 0) {
        suggestedDancesList.innerHTML = '<div class="no-suggestions">Searching for beats...</div>';
        return;
    }

    suggestedDancesList.innerHTML = '';
    suggestions.forEach(dance => {
        const btn = document.createElement('button');
        btn.className = 'suggestion-btn' + (activeDance && activeDance.name === dance.name ? ' active' : '');
        btn.innerHTML =
            '<span>' + dance.name + '</span>' +
            '<svg class="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
            '<polyline points="9 18 15 12 9 6"></polyline>' +
            '</svg>';
        btn.onclick = () => selectDance(dance);
        suggestedDancesList.appendChild(btn);
    });
}

function selectDance(dance) {
    activeDance = dance;
    danceStyleName.textContent = dance.name;
    updateDanceSuggestions(); // Refresh active state

    // Trigger immediate rhythm display update
    const countText = beatCount.textContent;
    const count = (countText === '--' || isNaN(parseInt(countText))) ? 0 : parseInt(countText);
    const totalBeats = ((measureCount - 1) * 4) + (count > 0 ? count - 1 : 0);
    updateRhythmDisplay(totalBeats);

    rhythmCard.classList.add('pulse');
    setTimeout(() => rhythmCard.classList.remove('pulse'), 200);
}

function updateRhythmDisplay(totalBeats) {
    if (!activeDance) {
        rhythmDisplay.textContent = "--";
        return;
    }

    const rhythm = activeDance.rhythm;
    const beatsPerPhrase = activeDance.beatsPerMeasure;

    // Use modular arithmetic to find current position in rhythm array
    const currentIdx = totalBeats % beatsPerPhrase;

    rhythmDisplay.innerHTML = rhythm.map((word, i) => {
        // Special highlighting for the current step
        const isCurrent = (i === currentIdx);
        return `<span class="rhythm-word ${isCurrent ? 'active' : ''}">${word}</span>`;
    }).join(' ');
}

// --- Frequency Visualizer ---

function drawVisualizer() {
    requestAnimationFrame(drawVisualizer);
    if (!analyser) return;

    _debugFrameCount++;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    // Throttled frame-level debug
    const now = Date.now();
    if ((now - _debugLastLogTime) > DEBUG_LOG_INTERVAL || _debugFrameCount === 1) {
        // Sample first 10 frequency bins
        const sample = Array.from(dataArray.slice(0, 10));
        const max = Math.max(...dataArray);
        const sum = dataArray.reduce((a, b) => a + b, 0);
        debugLog(
            `📊 Frame #${_debugFrameCount} | canvas: ${canvas.width}x${canvas.height} | ` +
            `bins: ${bufferLength} | max: ${max} | sum: ${sum} | ` +
            `first10: [${sample.join(',')}]`
        );
        if (max === 0 && _debugFrameCount > 60) {
            debugWarn('⚠️ All frequency data is ZERO — no audio is reaching the analyser!');
            debugWarn('  Check: Did you share tab audio? Is AudioContext state:', audioContext?.state);
        }
    }

    // Run beat detection every frame
    const beatInfo = detectBeat(dataArray, bufferLength);

    const width = canvas.width;
    const height = canvas.height;

    // Background — flash on beat
    if (isBeatFrame) {
        canvasCtx.fillStyle = 'rgba(130, 81, 238, 0.15)'; // var(--brand-subtle)
    } else {
        canvasCtx.fillStyle = '#0d0d0d';
    }
    canvasCtx.fillRect(0, 0, width, height);

    // Draw frequency bars with color-coded bands
    const usableBins = Math.min(bufferLength, Math.floor(width / 3));
    const barWidth = (width / usableBins) - 1;
    let x = 0;

    // Track peak frequency
    let peakVal = 0;
    let peakIdx = 0;

    for (let i = 0; i < usableBins; i++) {
        const barHeight = (dataArray[i] / 255) * height;
        const ratio = i / usableBins;

        if (dataArray[i] > peakVal) {
            peakVal = dataArray[i];
            peakIdx = i;
        }

        let r, g, b;
        if (ratio < 0.12) {
            // Bass — Magenta/Red
            r = 255; g = 20; b = 80 + (dataArray[i] * 0.5);
        } else if (ratio < 0.45) {
            // Mids — Cyan blend
            r = 0; g = Math.floor(160 + dataArray[i] * 0.35); b = 255;
        } else {
            // Highs — Cool white
            const brightness = 120 + dataArray[i] * 0.5;
            r = brightness * 0.8; g = brightness; b = brightness;
        }

        // Main bar
        if (ratio < 0.12) {
            canvasCtx.fillStyle = '#8251EE'; // Brand Purple (Bass)
        } else if (ratio < 0.45) {
            canvasCtx.fillStyle = '#06B6D4'; // Cyan (Mids)
        } else {
            canvasCtx.fillStyle = '#FFFFFF'; // White (Highs)
        }
        canvasCtx.fillRect(x, height - barHeight, barWidth, barHeight);

        // Subtle glow at bar tops
        if (barHeight > 5) {
            canvasCtx.fillStyle = `rgba(${Math.min(255, r)},${Math.min(255, g)},${Math.min(255, b)},0.4)`;
            canvasCtx.fillRect(x, height - barHeight - 2, barWidth, 2);
        }

        x += barWidth + 1;
    }

    // Beat marker flash line
    if (isBeatFrame) {
        canvasCtx.strokeStyle = 'rgba(130, 81, 238, 0.8)';
        canvasCtx.lineWidth = 2;
        canvasCtx.beginPath();
        canvasCtx.moveTo(0, 1);
        canvasCtx.lineTo(width, 1);
        canvasCtx.stroke();
    }

    // Peak frequency label
    if (peakVal > 20 && audioContext) {
        const peakFreq = Math.round((peakIdx / bufferLength) * (audioContext.sampleRate / 2));
        canvasCtx.fillStyle = 'rgba(255,255,255,0.5)';
        canvasCtx.font = '11px Inter, monospace';
        canvasCtx.fillText(`Peak: ${peakFreq} Hz`, width - 110, 16);
    }

    // --- Beat Timeline Strip (bottom 24px) ---
    drawBeatTimeline();
}

function drawBeatTimeline() {
    if (!timelineCanvas || !timelineCtx) return;

    const now = Date.now();
    const w = timelineCanvas.width;
    const h = timelineCanvas.height;

    // Background
    timelineCtx.fillStyle = '#0d0d0d';
    timelineCtx.fillRect(0, 0, w, h);

    // Center line
    timelineCtx.strokeStyle = 'rgba(255,255,255,0.05)';
    timelineCtx.lineWidth = 1;
    timelineCtx.beginPath();
    timelineCtx.moveTo(0, h / 2);
    timelineCtx.lineTo(w, h / 2);
    timelineCtx.stroke();

    // Clean old beats
    const TIMELINE_DURATION = 15000; // 15 seconds
    beatTimeline = beatTimeline.filter(b => now - b.time < TIMELINE_DURATION);

    // Draw beat markers
    for (const beat of beatTimeline) {
        const age = now - beat.time;
        const xPos = w - (age / TIMELINE_DURATION) * w;
        const opacity = 1 - (age / TIMELINE_DURATION);
        const barH = Math.max(4, Math.min(h * 0.8, h * 0.4 * beat.strength));

        timelineCtx.fillStyle = `rgba(130, 81, 238, ${opacity})`;
        timelineCtx.fillRect(xPos - 1, (h - barH) / 2, 2, barH);

        if (opacity > 0.8) {
            timelineCtx.shadowBlur = 10;
            timelineCtx.shadowColor = 'rgba(130, 81, 238, 0.5)';
            timelineCtx.fillRect(xPos - 1, (h - barH) / 2, 2, barH);
            timelineCtx.shadowBlur = 0;
        }
    }

    // "NOW" marker on the right edge
    tlCtx.fillRect(w - 2, 0, 2, h);

    // Time labels
    tlCtx.fillStyle = '#71717A'; // var(--text-muted)
    tlCtx.font = '10px Segoe UI, sans-serif';
    tlCtx.fillText('now', w - 24, h - 3);
    tlCtx.fillText(`-${TIMELINE_DURATION / 1000}s`, 4, h - 3);
}


init();
